import React, { Suspense, lazy, useEffect } from 'react';
import GameContext from './GameContext';
import PageController from './PageController';
import HowToPlayPage from './pages/howToPlay/HowToPlayPage';
import LandingPage from './pages/landing/LandingPage';
import TutorialPage from './pages/tutorial/TutorialPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import VersionPage from './pages/version/VersionPage';
import FaqPage from './pages/info/FaqPage';
import AboutPage from './pages/info/AboutPage';
import AmongUsIrlPage from './pages/seo/AmongUsIrlPage';
import HowToPlayIrlPage from './pages/seo/HowToPlayIrlPage';
import TaskIdeasPage from './pages/seo/TaskIdeasPage';
import TaskGeneratorPage from './pages/seo/TaskGeneratorPage';
import SocialDeductionPage from './pages/seo/SocialDeductionPage';
import BirthdayPartyPage from './pages/seo/BirthdayPartyPage';
import GroupGamesPage from './pages/seo/GroupGamesPage';
import AdultPartyPage from './pages/seo/AdultPartyPage';
import MurderMysteryPage from './pages/seo/MurderMysteryPage';
import AdultBirthdayPage from './pages/seo/AdultBirthdayPage';
import GameNightPage from './pages/seo/GameNightPage';
import FamilyCleanupPage from './pages/seo/FamilyCleanupPage';
import TraitorsAtHomePage from './pages/seo/TraitorsAtHomePage';
import PhonePartyPage from './pages/seo/PhonePartyPage';
import SleepoverPage from './pages/seo/SleepoverPage';
import FamilyReunionPage from './pages/seo/FamilyReunionPage';
import RoleGeneratorPage from './pages/seo/RoleGeneratorPage';
import AmongUsChoresPage from './pages/seo/AmongUsChoresPage';
import ChoreGamesPage from './pages/seo/ChoreGamesPage';
import FamilyCleaningRoutinePage from './pages/seo/FamilyCleaningRoutinePage';
import KidsPartyPage from './pages/seo/KidsPartyPage';
import YouthGroupsPage from './pages/seo/YouthGroupsPage';
import PrintableTaskCardsPage from './pages/seo/PrintableTaskCardsPage';
import HolidayPartyPage from './pages/seo/HolidayPartyPage';
import NotFoundPage from './pages/info/NotFoundPage';
import TranslatedPage from './pages/seo/translated/TranslatedPage';
import { matchTranslatedPath } from './seo/translatedPages';
import { getRoomCodeFromSearch } from './utils/inviteLinks';
import { captureAcquisition, noteVisit } from './seo/discovery';
import { usePageMeta } from './seo/usePageMeta';
import { applyDocumentLanguage } from './i18n';

const StylePreview = lazy(() => import('./preview/StylePreview'));

function getRoute() {
  const roomCode = getRoomCodeFromSearch();
  const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/';

  if (normalizedPath === '/make-cleaning-fun-for-kids' || normalizedPath.startsWith('/make-cleaning-fun-for-kids/')) {
    return 'family-cleanup';
  }

  if (normalizedPath === '/among-us-irl-task-generator' || normalizedPath.startsWith('/among-us-irl-task-generator/')) {
    return 'task-generator';
  }

  if (
    normalizedPath === '/play' ||
    normalizedPath.startsWith('/play/') ||
    Boolean(roomCode)
  ) {
    return 'game';
  }

  if (normalizedPath === '/how-to-play' || normalizedPath.startsWith('/how-to-play/')) {
    return 'how-to-play';
  }

  if (normalizedPath === '/tutorial' || normalizedPath.startsWith('/tutorial/')) {
    return 'tutorial';
  }

  if (normalizedPath === '/faq' || normalizedPath.startsWith('/faq/')) {
    return 'faq';
  }

  if (normalizedPath === '/about' || normalizedPath.startsWith('/about/')) {
    return 'about';
  }

  if (normalizedPath === '/among-us-irl' || normalizedPath.startsWith('/among-us-irl/')) {
    return 'among-us-irl';
  }

  if (normalizedPath === '/how-to-play-among-us-irl' || normalizedPath.startsWith('/how-to-play-among-us-irl/')) {
    return 'how-to-play-irl';
  }

  if (normalizedPath === '/among-us-irl-task-ideas' || normalizedPath.startsWith('/among-us-irl-task-ideas/')) {
    return 'task-ideas';
  }

  if (normalizedPath === '/dashboard' || normalizedPath.startsWith('/dashboard/')) {
    return 'dashboard';
  }

  if (normalizedPath === '/version' || normalizedPath.startsWith('/version/')) {
    return 'version';
  }

  if (normalizedPath === '/social-deduction-games' || normalizedPath.startsWith('/social-deduction-games/')) {
    return 'social-deduction-games';
  }

  if (normalizedPath === '/among-us-birthday-party' || normalizedPath.startsWith('/among-us-birthday-party/')) {
    return 'birthday-party';
  }

  if (normalizedPath === '/party-games-for-10-people' || normalizedPath.startsWith('/party-games-for-10-people/')) {
    return 'group-games';
  }

  if (normalizedPath === '/party-games-for-adults' || normalizedPath.startsWith('/party-games-for-adults/')) {
    return 'adult-party';
  }

  if (normalizedPath === '/murder-mystery-party-game' || normalizedPath.startsWith('/murder-mystery-party-game/')) {
    return 'murder-mystery';
  }

  if (normalizedPath === '/birthday-party-games-for-adults' || normalizedPath.startsWith('/birthday-party-games-for-adults/')) {
    return 'adult-birthday';
  }

  if (normalizedPath === '/game-night-ideas' || normalizedPath.startsWith('/game-night-ideas/')) {
    return 'game-night';
  }

  if (normalizedPath === '/traitors-at-home' || normalizedPath.startsWith('/traitors-at-home/')) {
    return 'traitors-at-home';
  }

  if (normalizedPath === '/party-games-on-your-phone' || normalizedPath.startsWith('/party-games-on-your-phone/')) {
    return 'phone-party';
  }

  if (normalizedPath === '/sleepover-games-for-teens' || normalizedPath.startsWith('/sleepover-games-for-teens/')) {
    return 'sleepover';
  }

  if (normalizedPath === '/indoor-family-reunion-games' || normalizedPath.startsWith('/indoor-family-reunion-games/')) {
    return 'family-reunion';
  }

  if (normalizedPath === '/among-us-role-generator' || normalizedPath.startsWith('/among-us-role-generator/')) {
    return 'role-generator';
  }

  if (normalizedPath === '/among-us-chores' || normalizedPath.startsWith('/among-us-chores/')) {
    return 'among-us-chores';
  }

  if (normalizedPath === '/chore-games-for-kids' || normalizedPath.startsWith('/chore-games-for-kids/')) {
    return 'chore-games';
  }

  if (normalizedPath === '/family-cleaning-routine' || normalizedPath.startsWith('/family-cleaning-routine/')) {
    return 'cleaning-routine';
  }

  if (normalizedPath === '/among-us-party-for-kids' || normalizedPath.startsWith('/among-us-party-for-kids/')) {
    return 'kids-party';
  }

  if (normalizedPath === '/among-us-irl-for-youth-groups' || normalizedPath.startsWith('/among-us-irl-for-youth-groups/')) {
    return 'youth-groups';
  }

  if (normalizedPath === '/among-us-task-cards-printable' || normalizedPath.startsWith('/among-us-task-cards-printable/')) {
    return 'printable-cards';
  }

  if (normalizedPath === '/holiday-party-games-for-adults' || normalizedPath.startsWith('/holiday-party-games-for-adults/')) {
    return 'holiday-party';
  }

  if (normalizedPath === '/' || normalizedPath === '/index.html') {
    return 'landing';
  }
  if (matchTranslatedPath(normalizedPath)) {
    return 'translated';
  }

  return 'not-found';
}

function GamePage() {
  usePageMeta({
    title: 'Sus Party | Host or Join a Game',
    description: 'Host a free Sus Party game or join friends with a room code. Play Among Us in real life with tasks, secret roles, sabotage, and votes.',
    canonical: 'https://susparty.com/play',
    documentClass: 'game-document',
  });
  useEffect(() => { applyDocumentLanguage(); }, []);
  return <GameContext><PageController /></GameContext>;
}

function App() {
    const route = getRoute();
    useEffect(() => { captureAcquisition(); noteVisit(); }, []);

  if (process.env.NODE_ENV === 'development' && window.location.pathname === '/style-preview') {
    return <Suspense fallback={<div>Loading preview…</div>}><StylePreview /></Suspense>;
  }

  if (route === 'game') {
    return <GamePage />;
  }

  if (route === 'how-to-play') {
    return <HowToPlayPage />;
  }

  if (route === 'tutorial') {
    return <TutorialPage />;
  }

  if (route === 'faq') {
    return <FaqPage />;
  }

  if (route === 'about') {
    return <AboutPage />;
  }

  if (route === 'among-us-irl') {
    return <AmongUsIrlPage />;
  }

  if (route === 'how-to-play-irl') {
    return <HowToPlayIrlPage />;
  }

  if (route === 'task-ideas') {
    return <TaskIdeasPage />;
  }

  if (route === 'task-generator') {
    return <TaskGeneratorPage />;
  }

  if (route === 'dashboard') {
    return <AdminDashboard />;
  }

  if (route === 'version') {
    return <VersionPage />;
  }

  if (route === 'translated') {
    const { lang, page } = matchTranslatedPath(window.location.pathname.replace(/\/+$/, ''));
    return <TranslatedPage lang={lang} page={page} />;
  }
  if (route === 'social-deduction-games') return <SocialDeductionPage />;
  if (route === 'birthday-party') return <BirthdayPartyPage />;
  if (route === 'group-games') return <GroupGamesPage />;
  if (route === 'adult-party') return <AdultPartyPage />;
  if (route === 'murder-mystery') return <MurderMysteryPage />;
  if (route === 'adult-birthday') return <AdultBirthdayPage />;
  if (route === 'game-night') return <GameNightPage />;
  if (route === 'family-cleanup') return <FamilyCleanupPage />;
  if (route === 'traitors-at-home') return <TraitorsAtHomePage />;
  if (route === 'phone-party') return <PhonePartyPage />;
  if (route === 'sleepover') return <SleepoverPage />;
  if (route === 'family-reunion') return <FamilyReunionPage />;
  if (route === 'role-generator') return <RoleGeneratorPage />;
  if (route === 'among-us-chores') return <AmongUsChoresPage />;
  if (route === 'chore-games') return <ChoreGamesPage />;
  if (route === 'cleaning-routine') return <FamilyCleaningRoutinePage />;
  if (route === 'kids-party') return <KidsPartyPage />;
  if (route === 'youth-groups') return <YouthGroupsPage />;
  if (route === 'printable-cards') return <PrintableTaskCardsPage />;
  if (route === 'holiday-party') return <HolidayPartyPage />;

  if (route === 'not-found') return <NotFoundPage />;
  return <LandingPage />;
}

export default App;
